import { getToken, clearToken } from '../utils/authToken';
/**
 * AdminDashboard - Panel de administración completo con gráficos
 * Solo visible para usuarios con is_admin=true
 */
import { useState, useEffect, useCallback } from 'react';
import { API_URL, API } from '../config';
import { X, Users, Activity, MessageSquare, TrendingUp, Clock, BarChart3, RefreshCw } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import axios from 'axios';


// Stat Card component
const StatCard = ({ icon: Icon, label, value, subValue, color, trend }) => (
  <div className={`p-4 rounded-xl bg-zinc-800/50 border border-zinc-700/50 hover:border-${color}-500/30 transition-colors`}>
    <div className="flex items-center justify-between">
      <div className={`p-2 rounded-lg bg-${color}-500/20`}>
        <Icon className={`w-5 h-5 text-${color}-400`} />
      </div>
      {trend !== undefined && (
        <span className={`text-xs px-2 py-0.5 rounded-full ${trend >= 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
          {trend >= 0 ? '+' : ''}{trend}%
        </span>
      )}
    </div>
    <div className="mt-3">
      <p className="text-2xl font-bold text-white">{value?.toLocaleString() || 0}</p>
      <p className="text-xs text-zinc-500 mt-1">{label}</p>
      {subValue && <p className="text-xs text-zinc-400 mt-0.5">{subValue}</p>}
    </div>
  </div>
);

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-3 shadow-xl">
        <p className="text-xs text-zinc-400 mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm font-medium" style={{ color: entry.color }}>
            {entry.name}: {entry.value?.toLocaleString()}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const AdminDashboard = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(30);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Delay chart rendering until modal is fully visible to prevent Recharts dimension warnings
  const [chartsReady, setChartsReady] = useState(false);
  
  const txt = {
    es: {
      title: 'Panel de Administración',
      subtitle: 'Estadísticas de la plataforma',
      totalUsers: 'Usuarios Totales',
      activeSessions: 'Sesiones Activas',
      totalConversations: 'Conversaciones Totales',
      newUsersInPeriod: 'Nuevos en el período',
      newConversations: 'Nuevas Conversaciones',
      messages: 'Mensajes',
      userGrowth: 'Crecimiento de Usuarios',
      usageTrends: 'Tendencias de Uso',
      hourlyActivity: 'Actividad por Hora',
      peakHours: 'Horas pico',
      last7Days: '7 días',
      last30Days: '30 días',
      last90Days: '90 días',
      loading: 'Cargando datos...',
      error: 'Error al cargar datos',
      retry: 'Reintentar',
      refresh: 'Actualizar',
      close: 'Cerrar',
      newUsers: 'Nuevos usuarios',
      totalUsersLabel: 'Total usuarios',
      conversations: 'Conversaciones',
      activity: 'Actividad'
    },
    en: {
      title: 'Admin Dashboard',
      subtitle: 'Platform statistics',
      totalUsers: 'Total Users',
      activeSessions: 'Active Sessions',
      totalConversations: 'Total Conversations',
      newUsersInPeriod: 'New in period',
      newConversations: 'New Conversations',
      messages: 'Messages',
      userGrowth: 'User Growth',
      usageTrends: 'Usage Trends',
      hourlyActivity: 'Hourly Activity',
      peakHours: 'Peak hours',
      last7Days: '7 days',
      last30Days: '30 days',
      last90Days: '90 days',
      loading: 'Loading data...',
      error: 'Error loading data',
      retry: 'Retry',
      refresh: 'Refresh',
      close: 'Close',
      newUsers: 'New users',
      totalUsersLabel: 'Total users',
      conversations: 'Conversations',
      activity: 'Activity'
    }
  };
  const t = txt[language] || txt.es;
  
  const fetchDashboardData = useCallback(async () => {
    try {
      setIsRefreshing(true);
      const token = getToken();
      if (!token) {
        setError('No token');
        return;
      }
      
      const response = await axios.get(`${API}/stats/dashboard?authorization=${token}&days=${period}`);
      setData(response.data);
      setError(null);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err.response?.data?.detail || 'Error fetching data');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [period]);
  
  useEffect(() => {
    if (isOpen) {
      fetchDashboardData();
      // Use a longer delay to ensure modal is fully visible before rendering charts
      // This prevents Recharts dimension warnings
      const timer = setTimeout(() => setChartsReady(true), 400);
      return () => clearTimeout(timer);
    } else {
      setChartsReady(false);
    }
  }, [isOpen, fetchDashboardData]);
  
  if (!isOpen) return null;
  
  // Format growth data for chart (show last N points based on screen)
  const formatGrowthData = () => {
    if (!data?.growth?.data) return [];
    const growthData = data.growth.data;
    // Show every Nth point to avoid crowding
    const step = Math.max(1, Math.floor(growthData.length / 15));
    return growthData.filter((_, i) => i % step === 0 || i === growthData.length - 1).map(d => ({
      ...d,
      date: d.date.slice(5) // Show MM-DD format
    }));
  };
  
  // Format usage data for chart
  const formatUsageData = () => {
    if (!data?.usage?.data) return [];
    const usageData = data.usage.data;
    const step = Math.max(1, Math.floor(usageData.length / 15));
    return usageData.filter((_, i) => i % step === 0 || i === usageData.length - 1).map(d => ({
      ...d,
      date: d.date.slice(5)
    }));
  };
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="admin-dashboard-overlay"
    >
      <div 
        className="w-full max-w-6xl max-h-[90vh] bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        data-testid="admin-dashboard"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-zinc-800 bg-gradient-to-r from-indigo-900/30 via-purple-900/30 to-pink-900/30 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-500/20 rounded-xl">
              <BarChart3 className="w-6 h-6 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">{t.title}</h2>
              <p className="text-xs text-zinc-400">{t.subtitle}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Period selector */}
            <div className="flex items-center gap-1 bg-zinc-800 rounded-lg p-1">
              {[7, 30, 90].map((days) => (
                <button
                  key={days}
                  onClick={() => setPeriod(days)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    period === days 
                      ? 'bg-indigo-600 text-white' 
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-700'
                  }`}
                  data-testid={`period-${days}`}
                >
                  {days === 7 ? t.last7Days : days === 30 ? t.last30Days : t.last90Days}
                </button>
              ))}
            </div>
            
            {/* Refresh button */}
            <button
              onClick={fetchDashboardData}
              disabled={isRefreshing}
              className="p-2 hover:bg-zinc-800 rounded-lg transition-colors disabled:opacity-50"
              title={t.refresh}
              data-testid="refresh-dashboard"
            >
              <RefreshCw className={`w-5 h-5 text-zinc-400 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
            
            {/* Close button */}
            <button
              onClick={onClose}
              className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
              data-testid="close-dashboard"
            >
              <X className="w-5 h-5 text-zinc-400 hover:text-white" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <RefreshCw className="w-8 h-8 text-indigo-400 animate-spin mx-auto mb-3" />
                <p className="text-zinc-400">{t.loading}</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <p className="text-red-400 mb-3">{t.error}: {error}</p>
                <button
                  onClick={fetchDashboardData}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-colors"
                >
                  {t.retry}
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard
                  icon={Users}
                  label={t.totalUsers}
                  value={data?.summary?.total_users}
                  subValue={`+${data?.summary?.new_users_period || 0} ${t.newUsersInPeriod}`}
                  color="emerald"
                />
                <StatCard
                  icon={Activity}
                  label={t.activeSessions}
                  value={data?.summary?.active_sessions}
                  color="cyan"
                />
                <StatCard
                  icon={MessageSquare}
                  label={t.totalConversations}
                  value={data?.summary?.total_conversations}
                  subValue={`+${data?.summary?.new_conversations_period || 0} ${t.newConversations.toLowerCase()}`}
                  color="purple"
                />
                <StatCard
                  icon={TrendingUp}
                  label={t.messages}
                  value={data?.usage?.total_messages}
                  color="amber"
                />
              </div>
              
              {/* Charts Row 1 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* User Growth Chart */}
                <div className="p-5 rounded-xl bg-zinc-800/50 border border-zinc-700/50">
                  <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-emerald-400" />
                    {t.userGrowth}
                  </h3>
                  <div className="h-64" style={{ minWidth: 0 }}>
                    {chartsReady && (
                      <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                        <AreaChart data={formatGrowthData()}>
                          <defs>
                            <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 10 }} axisLine={{ stroke: '#4b5563' }} />
                          <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} axisLine={{ stroke: '#4b5563' }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Area 
                            type="monotone" 
                            dataKey="total_users" 
                            name={t.totalUsersLabel}
                            stroke="#10b981" 
                            fillOpacity={1} 
                            fill="url(#colorUsers)" 
                          />
                          <Line 
                            type="monotone" 
                            dataKey="new_users" 
                            name={t.newUsers}
                            stroke="#6366f1" 
                            strokeWidth={2}
                            dot={false}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
                
                {/* Usage Trends Chart */}
                <div className="p-5 rounded-xl bg-zinc-800/50 border border-zinc-700/50">
                  <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-purple-400" />
                    {t.usageTrends}
                  </h3>
                  <div className="h-64" style={{ minWidth: 0 }}>
                    {chartsReady && (
                      <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                        <LineChart data={formatUsageData()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 10 }} axisLine={{ stroke: '#4b5563' }} />
                          <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} axisLine={{ stroke: '#4b5563' }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend wrapperStyle={{ paddingTop: '10px' }} />
                          <Line 
                            type="monotone" 
                            dataKey="conversations" 
                            name={t.conversations}
                            stroke="#a855f7" 
                            strokeWidth={2}
                            dot={false}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="messages" 
                            name={t.messages}
                            stroke="#f59e0b" 
                            strokeWidth={2}
                            dot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Hourly Activity Chart */}
              <div className="p-5 rounded-xl bg-zinc-800/50 border border-zinc-700/50">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-white flex items-center gap-2">
                    <Clock className="w-4 h-4 text-cyan-400" />
                    {t.hourlyActivity}
                  </h3>
                  {data?.hourly_activity?.peak_hours?.length > 0 && (
                    <span className="text-xs text-zinc-400">
                      {t.peakHours}: {data.hourly_activity.peak_hours.map(h => `${h}:00`).join(', ')}
                    </span>
                  )}
                </div>
                <div className="h-48" style={{ minWidth: 0 }}>
                  {chartsReady && (
                    <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={150}>
                      <BarChart data={data?.hourly_activity?.data || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                        <XAxis 
                          dataKey="label" 
                          tick={{ fill: '#9ca3af', fontSize: 9 }} 
                          axisLine={{ stroke: '#4b5563' }}
                          interval={1}
                        />
                        <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} axisLine={{ stroke: '#4b5563' }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar 
                          dataKey="activity" 
                          name={t.activity}
                          fill="#06b6d4" 
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-3 border-t border-zinc-800 bg-zinc-900/50 flex justify-between items-center shrink-0">
          <p className="text-xs text-zinc-500">
            {data?.timestamp && `${language === 'es' ? 'Última actualización' : 'Last updated'}: ${new Date(data.timestamp).toLocaleString()}`}
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
            data-testid="close-dashboard-footer"
          >
            {t.close}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
